import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import Draggable from 'components/draggable/Draggable'
import Dropdown, { IDropdownItem } from 'components/dropdown/Dropdown'
import Button from 'components/button/Button'
import Hamburger from 'assets/icons/hamburger.svg'
import Plus from 'assets/icons/plus.svg'
import { IProps as ITableCellEditorParams } from './TableCellEditor'

import createError from 'utils/createError'

import styles from './stringListInput.scss'
import booleanListStyles from './booleanListInput.scss'

interface IProps extends ITableCellEditorParams {
  initialValue: boolean[] | null
}

interface IState {
  value: boolean[] | null
  newItem: IDropdownItem | null
  draggedOverIdx: number
}

class BooleanListInput extends React.PureComponent<IProps, IState> {
  private items: Array<React.RefObject<HTMLInputElement>> = []
  private draggedItem: React.RefObject<HTMLInputElement> | null = null

  constructor(props: IProps) {
    super(props)

    const { initialValue } = props

    if (!Array.isArray(initialValue)) {
      throw createError(
        'BooleanListInput.constructor',
        'Invalid initialValue',
        {
          initialValue,
        }
      )
    }

    this.state = {
      value: initialValue,
      newItem: null,
      draggedOverIdx: -1,
    }

    this.items = Array.from({
      length: Math.max(initialValue?.length ?? 0, 1),
    }).map(() => React.createRef())

    this.items.push(React.createRef()) // And one more for the `newItem` input
  }

  // Called by ag-grid to get the final value when editing is finished
  getValue = () => this.state.value

  focus = () => {
    const { value } = this.state

    if (!value) return

    this.items[Math.max(value.length - 1, 0)].current?.focus() // Focus the last item
  }

  // Handle a change in one of the items
  handleChangeItem = (changedItem: IDropdownItem, idx: number) => {
    const { value } = this.state

    if (!Array.isArray(value)) {
      throw createError('BooleanListInput.handleChangeItem', 'Invalid value', {
        value,
        changedItem,
      })
    }

    // Replace value at idx with new value
    const newValue = [...value]
    newValue.splice(idx, 1, changedItem.id === 'true')
    return this.setState({
      value: newValue,
    })
  }

  handleChangeNewItem = (e: IDropdownItem) => {
    this.setState(
      {
        newItem: e,
      },
      () => setTimeout(() => this.handleAddNewItem(), 0)
      // We put this in a `0` setTimeout so it executes in the next tick
    )
  }

  handleAddNewItem = () => {
    const { value, newItem } = this.state

    if (!newItem) {
      return
    }

    if (!Array.isArray(value)) {
      throw createError('BooleanListInput.handleAddNewItem', 'Invalid value', {
        value,
        newItem,
      })
    }

    // Create a new ref for this new item
    this.items.push(React.createRef())

    this.setState(
      {
        value: [...value, newItem.id === 'true'],
        newItem: null,
      },
      () => {
        // Focus and scroll to new item
        this.items[this.items.length - 2]?.current?.focus()
        this.items[this.items.length - 2].current?.scrollIntoView(false)
      }
    )
  }

  handleRemoveItem = (idx: number) => {
    const { value } = this.state

    if (!Array.isArray(value)) {
      throw createError('BooleanListInput.handleRemoveClick', 'Invalid Value', {
        value,
        idx,
      })
    }

    this.items.splice(idx, 1)

    const valueCopy = [...value]
    valueCopy.splice(idx, 1) // Remove the item from value state

    // We put this setState in a `0` setTimeout so it executes in the next tick
    // This is necessary so the DOM element does not get removed before `handleClickOutside` in `TableCellEditor` has a chance to trace its ancestry to figure out if the "remove" button click was inside thee tooltip or not (DOMNode.contains())
    setTimeout(() => this.setState({ value: valueCopy }), 0)
  }

  handleDragStart = (idx: number) => {
    this.draggedItem = this.items[idx]
  }

  handleDragOver = (idx: number, e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault()
    this.setState({
      draggedOverIdx: idx,
    })
  }

  handleDragEnd = (idx: number) => {
    if (!this.draggedItem) {
      return
    }

    const value = this.state.value ?? []

    if (!Array.isArray(value)) {
      throw createError('BooleanListInput.HandleDragEnd', 'Invalid value', {
        value,
        idx,
      })
    }

    const changedItem = String(this.draggedItem.current?.value) // Dragged item value
    const valueCopy = [...value]

    this.items.splice(idx, 1) // Remove the item
    valueCopy.splice(idx, 1) // Remove the dragged item value from value state

    if (this.state.draggedOverIdx > idx) {
      // When you reorder downwards
      this.items.splice(this.state.draggedOverIdx - 1, 0, this.draggedItem) // Insert it infront of the last passed index
      valueCopy.splice(this.state.draggedOverIdx - 1, 0, changedItem === 'true') // Insert item value
    } else {
      this.items.splice(this.state.draggedOverIdx, 0, this.draggedItem)
      valueCopy.splice(this.state.draggedOverIdx, 0, changedItem === 'true')
    }

    this.draggedItem = null
    this.setState({
      value: valueCopy,
      draggedOverIdx: -1,
    })
  }

  render() {
    const { value } = this.state

    if (!Array.isArray(value)) {
      throw createError('BooleanListInput.render', 'Invalid value', {
        value,
      })
    }

    return (
      <>
        <div data-cy="input--boolean-list" className={styles.container}>
          <input
            type="text"
            className={styles.input}
            value={JSON.stringify(value)}
            readOnly
          />
          <ul
            className={cn(
              styles.itemContainer,
              booleanListStyles.itemContainer,
              'ag-theme-dark'
            )}
          >
            <div className={styles.itemScrollContainer}>
              {/* This separator only exists to illuminate when you drag over the first item (to indicate where you will drop) */}
              <div
                className={cn(styles.separator, {
                  [styles.draggedOver]: this.state.draggedOverIdx === 0,
                })}
              />
              {value.map((v, idx) => (
                <React.Fragment key={idx}>
                  <ListItem
                    innerRef={this.items[idx]}
                    suggestions={[
                      { id: 'true', label: 'true' },
                      { id: 'false', label: 'false' },
                    ]}
                    value={{ id: String(v), label: String(v) }}
                    invalid={v !== true && v !== false}
                    onChange={e => this.handleChangeItem(e, idx)}
                    onRemove={this.handleRemoveItem.bind(this, idx)}
                    onDragStart={this.handleDragStart.bind(this, idx)}
                    onDragEnd={this.handleDragEnd.bind(this, idx)}
                    onDragOver={this.handleDragOver.bind(this, idx)}
                  />
                  <div
                    className={cn(styles.separator, {
                      [styles.draggedOver]:
                        this.state.draggedOverIdx === idx + 1,
                    })}
                  />
                </React.Fragment>
              ))}
            </div>

            <ListItem
              key={value.length}
              innerRef={this.items[value.length]} // Theres one more item than value
              suggestions={[
                { id: '', label: '' },
                { id: 'true', label: 'true' },
                { id: 'false', label: 'false' },
              ]}
              value={{ id: '', label: '' }}
              invalid={false}
              onChange={e => this.handleChangeNewItem(e)}
              onAdd={this.handleAddNewItem}
              onDragOver={this.handleDragOver.bind(this, value.length)}
            />
          </ul>
        </div>
      </>
    )
  }
}

/**
 * Single item in the list editor
 */
class ListItem extends React.Component<{
  innerRef: React.RefObject<any>
  suggestions: IDropdownItem[]
  value: IDropdownItem
  invalid: boolean
  tabIndex?: number
  onChange: (item: IDropdownItem) => void
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragOver?: (e: React.DragEvent<HTMLLIElement>) => void
  onRemove?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
}> {
  render() {
    return (
      <li
        className={cn(styles.item, { [styles.invalid]: this.props.invalid })}
        onDragOver={this.props.onDragOver || undefined}
      >
        {this.props.onDragStart && this.props.onDragEnd && (
          <Draggable
            className={styles.dragButton}
            onDragStart={this.props.onDragStart}
            onDragEnd={this.props.onDragEnd}
          >
            <Hamburger />
          </Draggable>
        )}
        <Dropdown
          innerRef={this.props.innerRef}
          data-cy="input"
          className={booleanListStyles.itemDropdown}
          type="text"
          nativeSelect
          items={this.props.suggestions}
          selectedItem={this.props.value}
          onSelect={e => this.props.onChange(e)}
        />

        {this.props.onAdd && (
          <Button
            data-cy="input--boolean-list__add-btn"
            onClick={this.props.onAdd}
            className={styles.addScalarListItemBtn}
          >
            Add
          </Button>
        )}
        {this.props.onRemove && (
          <Button
            data-cy="input--boolean-list__remove-btn"
            onClick={e => this.props.onRemove?.(e)}
            className={styles.closeButton}
          >
            <Plus />
          </Button>
        )}
      </li>
    )
  }
}

export default observer(BooleanListInput)
